import { useCallback, useId, useMemo, useRef, type ChangeEvent } from "react";
import { useTranslation } from "react-i18next";
import { Loader2, Upload, X } from "lucide-react";
import { CollapsibleSection } from "@/components/CollapsibleSection";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/components/ui/use-toast";
import { OperationProgressStatus } from "@/components/progress";
import {
  AttachmentsDisplaySection,
  type AttachmentFileItem,
  type AttachmentGroup,
} from "@/components/AttachmentsDisplaySection";
import {
  ACCEPT_EXTENSIONS_STRING,
  AGREEMENT_PAPER_SCAN_FIELD_NAME,
  AGREEMENT_PAPER_SCANS_MAX_ACTIVE,
  ATTACHMENT_MAX_FILE_SIZE_MB,
  Module,
  NO_DATA,
} from "@/constants";
import { normalizeS3Key } from "@/lib/utils";
import { formatDateTime } from "@/lib/utils/formatters";
import type { UploadedFile } from "@/lib/file-storage/types";
import type { AgreementPaperScan } from "@/schemas/agreement";
import { useModulePermissions } from "@/modules/account/hooks/useModulePermissions";
import { useAccountRoleCheck } from "@/modules/account/hooks/useAccountRoleCheck";
import { useAgreementContext } from "@/modules/agreement/context";
import { useAgreementData } from "@/modules/agreement/hooks/useAgreementData";
import {
  toFileDownloadsKey,
  useAgreementFileDownloads,
} from "@/modules/agreement/hooks/useAgreementFileDownloads";
import { useAgreementFileUrl } from "@/modules/agreement/hooks/useAgreementFileUrl";
import {
  PaperScanCompletionError,
  useAgreementPaperScans,
} from "@/modules/agreement/hooks/useAgreementPaperScans";
import { canReadAgreementDocuments } from "@/modules/agreement/permissions/agreementDocumentsAccess";
import {
  canManagePaperScans,
  isPaperScansSectionVisible,
} from "@/modules/agreement/permissions/agreementPaperScansAccess";
import {
  describePaperScanError,
  shouldRefreshAgreementAfter,
} from "@/modules/agreement/utils/paperScanErrors";
import {
  useQuestionnaireAttachmentDeletion,
  useQuestionnaireAttachmentUpload,
  type AttachmentItem,
} from "@/modules/agreement/questionnaires/hooks/attachment";
import { QuestionnaireAttachmentDeleteDialog } from "@/modules/agreement/questionnaires/components/shared/attachment/QuestionnaireAttachmentDeleteDialog";
import { QuestionnaireAttachmentProgressDialog } from "@/modules/agreement/questionnaires/components/shared/attachment/QuestionnaireAttachmentProgressDialog";
import { QuestionnaireAttachmentUploadDialog } from "@/modules/agreement/questionnaires/components/shared/attachment/QuestionnaireAttachmentUploadDialog";
import { AgreementFileDownloadsInfo } from "../shared/AgreementFileDownloadsInfo";

/** Skan z rejestru jako element listy `AttachmentsDisplaySection`. */
const toAttachmentFileItem = (
  scan: AgreementPaperScan,
): AttachmentFileItem => ({
  filename: scan.filename,
  path: scan.path,
  size: scan.size ?? undefined,
});

/** Skan jako element, którego oczekuje `useQuestionnaireAttachmentDeletion.handleRemove`. */
const toAttachmentItem = (
  scan: AgreementPaperScan,
  index: number,
): AttachmentItem => {
  const s3Key = normalizeS3Key(scan.path);
  return {
    index,
    key: scan.id,
    field: { id: scan.id, filename: scan.filename, s3_key: s3Key },
    meta: { id: scan.id, filename: scan.filename, s3Key, canDelete: true },
    url: null,
  };
};

/** Wynik wsadu z `useQuestionnaireAttachmentUpload.onBatchFinished`. */
interface UploadBatchResult {
  uploaded: number;
  failedUploads: { file: File; error: unknown }[];
}

/**
 * Sekcja „Załączniki" w szczegółach umowy (ASMMNT-83, etap 2): skany/zdjęcia podpisanej
 * umowy papierowej obcokrajowca. Dla umowy Polaka sekcji nie ma — wrapper sprawdza to,
 * zanim zbuduje klienta presigned i resztę hooków.
 */
export const AgreementPaperScansSection = () => {
  const { non_draft } = useAgreementData();
  if (!isPaperScansSectionVisible(non_draft.citizenship_name)) {
    return null;
  }
  return <AgreementPaperScansSectionContent />;
};

/**
 * Dodanie pierwszego aktywnego skanu ustawia checkbox rekrutera na TAK, usunięcie ostatniego —
 * na NIE (backend zwraca pola potwierdzeń w odpowiedzi `complete` / `complete-delete`, hook
 * wpisuje je do cache). „Dodaj"/„Usuń" tylko z `agreements_paper_confirm_recruiter`; pobranie
 * i podgląd wg tej samej reguły co „Dokumenty" (`canReadAgreementDocuments`), przez endpoint
 * rejestrujący zdarzenia. Usunięte skany są wyszarzone, bez podglądu i pobrania.
 * Gdy serwer zna inny stan niż strona (409 z nieświeżej strony, odpowiedź o złym kształcie),
 * szczegóły są odświeżane po cichu, raz po wsadzie — bez loadera całej strony.
 */
const AgreementPaperScansSectionContent = () => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { agreementId } = useAgreementContext();
  const { paper_scans, file_downloads, file_download_totals, non_draft } =
    useAgreementData();
  const permissions = useModulePermissions(Module.Agreements);
  const { role } = useAccountRoleCheck();
  const { presignedClient, applyScanMutation, refreshAgreement } =
    useAgreementPaperScans(agreementId);
  const { resolveFileUrl } = useAgreementFileUrl(agreementId);
  const downloadsByPath = useAgreementFileDownloads(
    file_downloads,
    file_download_totals,
  );
  const addButtonRef = useRef<HTMLButtonElement>(null);
  const slotsHintId = useId();
  const deletedTitleId = useId();
  // Stan jednego wsadu: nazwy skanów wpisanych do cache (toast zbiorczy) i flaga złego kształtu
  // odpowiedzi `complete` (serwer przyjął plik; szczegóły odświeżamy raz, po wsadzie - nie per plik).
  const batchRef = useRef({ applied: [] as string[], needsRefresh: false });

  // Starszy backend nie zwraca `paper_scans`; pole jest opcjonalne w schemacie, więc `?? []`.
  const scans = useMemo(() => paper_scans ?? [], [paper_scans]);
  const activeScans = useMemo(
    () => scans.filter((scan) => scan.state === "active"),
    [scans],
  );
  const deletedScans = useMemo(
    () => scans.filter((scan) => scan.state === "deleted"),
    [scans],
  );
  const activeScansByPath = useMemo(
    () =>
      new Map(
        activeScans.map((scan, index) => [
          normalizeS3Key(scan.path),
          { scan, index },
        ]),
      ),
    [activeScans],
  );

  const citizenshipName = non_draft.citizenship_name;
  const canManage = canManagePaperScans({
    hasPermission: permissions.agreements_paper_confirm_recruiter === true,
    citizenshipName,
  });
  const canRead = canReadAgreementDocuments({
    hasDocumentsReadPermission: permissions.agreements_documents_read,
    roleCode: role,
    citizenshipName,
  });
  const freeSlots = Math.max(
    0,
    AGREEMENT_PAPER_SCANS_MAX_ACTIVE - activeScans.length,
  );

  // Czysty opis do toastu; odświeżenie szczegółów idzie osobno, raz na wsad / raz na usunięcie.
  const describeError = useCallback(
    (error: unknown) => describePaperScanError(error, t),
    [t],
  );

  /** Wpisuje odpowiedź complete/complete-delete do cache; `null` = zły kształt (serwer już zmienił stan). */
  const applyCompletion = useCallback(
    (completionResponse: unknown): AgreementPaperScan | null => {
      try {
        return applyScanMutation(completionResponse).scan;
      } catch (error) {
        if (error instanceof PaperScanCompletionError) return null;
        throw error;
      }
    },
    [applyScanMutation],
  );

  const onFileUploaded = useCallback(
    (_file: File, stored: UploadedFile) => {
      const scan = applyCompletion(stored.completionResponse);
      if (scan) {
        batchRef.current.applied.push(scan.filename);
      } else {
        batchRef.current.needsRefresh = true;
      }
    },
    [applyCompletion],
  );

  const onBatchFinished = useCallback(
    ({ failedUploads }: UploadBatchResult) => {
      const { applied, needsRefresh } = batchRef.current;
      batchRef.current = { applied: [], needsRefresh: false };
      // Przy błędach ważniejszy jest toast błędu z hooka: TOAST_LIMIT = 1, kolejny by go zasłonił.
      if (failedUploads.length === 0) {
        if (applied.length === 1) {
          toast({
            title: t("agreements.paperScans.uploadSuccess", {
              filename: applied[0],
            }),
          });
        } else if (applied.length > 1) {
          toast({
            title: t("agreements.paperScans.uploadBatchSuccess", {
              count: applied.length,
            }),
          });
        }
        if (needsRefresh) {
          // Operacja po stronie serwera się udała: informacja, nie błąd pliku.
          toast({ title: t("agreements.paperScans.appliedWithRefresh") });
        }
      }
      // 404 (umowa poza strukturą), 409 „Polak"/limit/stan skanu: serwer zna inny stan niż strona.
      if (
        needsRefresh ||
        failedUploads.some(({ error }) => shouldRefreshAgreementAfter(error))
      ) {
        void refreshAgreement();
      }
    },
    [refreshAgreement, toast, t],
  );

  const {
    fileInputRef,
    operationProgress,
    pendingUpload,
    confirmUpload,
    cancelUpload,
    handleAddFilesClick,
    handleFileUpload,
  } = useQuestionnaireAttachmentUpload({
    name: AGREEMENT_PAPER_SCAN_FIELD_NAME,
    presignedClient,
    onFileUploaded,
    maxFiles: freeSlots,
    describeUploadError: describeError,
    onBatchFinished,
  });

  const onFileDeleted = useCallback(
    (_s3Key: string, _index: number, completionResponse: unknown) => {
      const scan = applyCompletion(completionResponse);
      if (scan) {
        toast({
          title: t("agreements.paperScans.deleteSuccess", {
            filename: scan.filename,
          }),
        });
        return;
      }
      toast({ title: t("agreements.paperScans.appliedWithRefresh") });
      void refreshAgreement();
    },
    [applyCompletion, refreshAgreement, toast, t],
  );

  const onDeleteError = useCallback(
    (error: unknown) => {
      if (shouldRefreshAgreementAfter(error)) {
        void refreshAgreement();
      }
    },
    [refreshAgreement],
  );

  const {
    pendingDelete,
    confirmDelete,
    cancelDelete,
    handleRemove,
    isDeleting,
  } = useQuestionnaireAttachmentDeletion({
    presignedClient,
    name: AGREEMENT_PAPER_SCAN_FIELD_NAME,
    onFileDeleted,
    describeDeleteError: describeError,
    onDeleteError,
  });

  const isUploading = operationProgress.isBusy();
  // Jeden plik: bez okna postępu (pasek 0→100 i nieaktywne „Anuluj" nic nie wnoszą) — stan na przycisku.
  // Wsad: okno postępu, którego nie da się zamknąć w trakcie.
  const isSingleUpload = isUploading && operationProgress.totalCount === 1;
  const showUploadDialog =
    operationProgress.status !== OperationProgressStatus.IDLE &&
    operationProgress.totalCount > 1;
  const isBusy = isUploading || isDeleting;

  const handleAddClick = useCallback(() => {
    if (isBusy) return;
    handleAddFilesClick();
  }, [handleAddFilesClick, isBusy]);

  /** Więcej plików niż wolnych miejsc: własny komunikat zamiast ogólnego limitu wsadu z walidatora. */
  const handleFilesSelected = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const selected = event.target.files?.length ?? 0;
      if (selected > freeSlots) {
        event.target.value = "";
        toast({
          title: t("fileAttachments.uploadErrorTitle"),
          description: t("agreements.paperScans.tooManyFiles", {
            selected,
            free: freeSlots,
            maxFiles: AGREEMENT_PAPER_SCANS_MAX_ACTIVE,
          }),
          variant: "destructive",
        });
        return;
      }
      void handleFileUpload(event);
    },
    [freeSlots, handleFileUpload, toast, t],
  );

  const renderFileMeta = useCallback(
    (file: AttachmentFileItem) => {
      const entry = file.path
        ? activeScansByPath.get(normalizeS3Key(file.path))
        : undefined;
      return (
        <span className="flex flex-col gap-0.5">
          {entry && (
            <span>
              {t("agreements.paperScans.addedBy", {
                date: formatDateTime(entry.scan.uploaded_at),
                name: entry.scan.created_by_name || NO_DATA,
              })}
            </span>
          )}
          <AgreementFileDownloadsInfo
            filename={file.filename}
            summary={
              file.path
                ? downloadsByPath.get(toFileDownloadsKey(file.path))
                : undefined
            }
          />
        </span>
      );
    },
    [activeScansByPath, downloadsByPath, t],
  );

  const renderFileActions = useCallback(
    (file: AttachmentFileItem) => {
      if (!canManage || !file.path) return null;
      const entry = activeScansByPath.get(normalizeS3Key(file.path));
      if (!entry) return null;
      const isDeletingThis =
        isDeleting && pendingDelete?.attachmentId === entry.scan.id;
      return (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          // Odstęp od „Podgląd" i 36 px na dotyku: „Usuń" nie może być pod palcem obok podglądu.
          // aria-disabled zamiast disabled: przycisk nie traci fokusu, gdy inna operacja trwa.
          className="ml-2 h-9 w-9 text-red-500 hover:bg-red-50 hover:text-red-600 touch-manipulation sm:h-6 sm:w-6 aria-disabled:cursor-not-allowed aria-disabled:opacity-50"
          // Nazwa z plikiem: czytnik ekranu rozróżnia przyciski „Usuń" w kolejnych wierszach.
          aria-label={t("agreements.paperScans.deleteFile", {
            filename: file.filename,
          })}
          aria-disabled={isBusy || undefined}
          aria-busy={isDeletingThis || undefined}
          onClick={() => {
            if (isBusy) return;
            handleRemove(toAttachmentItem(entry.scan, entry.index));
          }}
        >
          {isDeletingThis ? (
            <Loader2
              className="h-3.5 w-3.5 animate-spin motion-reduce:animate-none"
              aria-hidden="true"
            />
          ) : (
            <X className="h-3.5 w-3.5" aria-hidden="true" />
          )}
        </Button>
      );
    },
    [
      activeScansByPath,
      canManage,
      handleRemove,
      isBusy,
      isDeleting,
      pendingDelete,
      t,
    ],
  );

  const groups: AttachmentGroup[] = [
    {
      key: "paper_scans",
      title: t("agreements.paperScans.listTitle"),
      files: activeScans.map(toAttachmentFileItem),
    },
  ];

  // Obok „Dodaj": liczba wolnych miejsc, a przy limicie przyczyna blokady - oba powiązane z przyciskiem.
  const slotsHint =
    freeSlots === 0
      ? t("agreements.paperScans.limitReached", {
          maxFiles: AGREEMENT_PAPER_SCANS_MAX_ACTIVE,
        })
      : activeScans.length > 0
        ? t("agreements.paperScans.freeSlots", {
            free: freeSlots,
            maxFiles: AGREEMENT_PAPER_SCANS_MAX_ACTIVE,
          })
        : null;

  return (
    <CollapsibleSection title={t("agreements.paperScans.title")}>
      <div className="space-y-3">
        {/* Opis w treści, nie w nagłówku sekcji: tam nie zawija się na telefonie. */}
        <p className="text-xs text-gray-600">
          {t("agreements.paperScans.hint", {
            maxSize: ATTACHMENT_MAX_FILE_SIZE_MB,
            maxFiles: AGREEMENT_PAPER_SCANS_MAX_ACTIVE,
          })}
        </p>

        {canManage && (
          <div className="flex flex-wrap items-center gap-3">
            <Input
              ref={fileInputRef}
              type="file"
              multiple
              accept={ACCEPT_EXTENSIONS_STRING}
              onChange={handleFilesSelected}
              className="hidden"
              data-testid="paper-scan-file-input"
            />
            <Button
              ref={addButtonRef}
              type="button"
              variant="outline"
              size="sm"
              className="aria-disabled:cursor-progress aria-disabled:opacity-70"
              onClick={handleAddClick}
              disabled={freeSlots === 0}
              aria-disabled={isBusy || undefined}
              aria-busy={isSingleUpload || undefined}
              aria-describedby={slotsHint ? slotsHintId : undefined}
            >
              {isSingleUpload ? (
                <Loader2
                  className="h-4 w-4 animate-spin motion-reduce:animate-none"
                  aria-hidden="true"
                />
              ) : (
                <Upload className="h-4 w-4" aria-hidden="true" />
              )}
              {isSingleUpload
                ? t("agreements.paperScans.uploading")
                : t("agreements.paperScans.add")}
            </Button>
            {/* Komunikat dla czytników ekranu o trwającym przesyłaniu jednego pliku (bez okna postępu). */}
            <span role="status" aria-live="polite" className="sr-only">
              {isSingleUpload && t("agreements.paperScans.uploading")}
            </span>
            {slotsHint && (
              <span id={slotsHintId} className="text-xs text-gray-600">
                {slotsHint}
              </span>
            )}
          </div>
        )}

        {activeScans.length > 0 ? (
          <AttachmentsDisplaySection
            groups={groups}
            canRead={canRead}
            resolveFileUrl={resolveFileUrl}
            renderFileMeta={renderFileMeta}
            renderFileActions={renderFileActions}
          />
        ) : (
          <p className="py-1 text-center text-xs text-gray-600">
            {t("agreements.paperScans.empty")}
          </p>
        )}

        {deletedScans.length > 0 && (
          <div className="space-y-1 px-2">
            <p
              id={deletedTitleId}
              className="text-xs font-medium text-gray-600"
            >
              {t("agreements.paperScans.deletedTitle", {
                count: deletedScans.length,
              })}
            </p>
            <ul
              className="space-y-1"
              aria-labelledby={deletedTitleId}
              data-testid="paper-scans-deleted"
            >
              {deletedScans.map((scan) => (
                <li key={scan.id} className="text-xs italic text-gray-500">
                  <span className="font-medium line-through break-all">
                    {scan.filename}
                  </span>
                  {" — "}
                  {t("agreements.paperScans.addedBy", {
                    date: formatDateTime(scan.uploaded_at),
                    name: scan.created_by_name || NO_DATA,
                  })}
                  {"; "}
                  {t("agreements.paperScans.deletedBy", {
                    date: formatDateTime(scan.deleted_at),
                    name: scan.deleted_by_name || NO_DATA,
                  })}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      <QuestionnaireAttachmentProgressDialog
        open={showUploadDialog}
        operationProgress={operationProgress}
        currentItemLabel={operationProgress.currentItemLabel}
        translationKey="fileAttachments.uploadProgress"
        onClose={operationProgress.reset}
        returnFocusRef={addButtonRef}
        preventCloseWhileBusy
      />
      <QuestionnaireAttachmentUploadDialog
        open={Boolean(pendingUpload)}
        fileCount={pendingUpload?.length ?? 0}
        onConfirm={confirmUpload}
        onClose={cancelUpload}
      />
      <QuestionnaireAttachmentDeleteDialog
        open={Boolean(pendingDelete)}
        filename={pendingDelete?.filename}
        title={t("agreements.paperScans.confirmDeleteTitle")}
        description={t(
          activeScans.length === 1
            ? "agreements.paperScans.confirmDeleteLast"
            : "agreements.paperScans.confirmDeleteDescription",
        )}
        isBusy={isDeleting}
        returnFocusRef={addButtonRef}
        onConfirm={confirmDelete}
        onClose={cancelDelete}
      />
    </CollapsibleSection>
  );
};
